import { Product, Profile, scaleImage } from '@news-mono/web-common'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { IconVideo2, SevenNewsSection } from '../..'
import { mapListVideo } from '../../../../../libs/web-common/src/__api-mapping/v4/list-video'
import { StyledSharingWrap } from '../../bauhaus/BauhausHeroCard/BauhausHeroCard.styled'
import { Sharing } from '../../buttons/Sharing/Sharing'
import { StyledIconVideo2 } from '../../cards/Kicker/Kicker'
import {
    StyledKicker,
    StyledKickerText,
} from '../../cards/Kicker/Kicker.styled'
import { PlayOverlay } from '../../content/Video/components/PlayOverlay/PlayOverlay'
import { TitleOverlay } from '../../content/Video/components/TitleOverlay/TitleOverlay'
import { UnmuteButton } from '../../content/Video/components/UnmuteButton/UnmuteButton'
import { RenderPlayerParams } from '../../content/Video/player-render-params'
import { getHumanDuration } from '../../content/Video/utils/get-human-duration'
import { isBauhausSection } from '../../content/Video/utils/is-bauhaus'
import { isMobileViewport } from '../../content/Video/utils/is-mobile-viewport'
import { getPosterImage } from '../../content/Video/utils/poster'
import { getImageWidth } from '../../content/Video/utils/product-info'
import {
    StyledVideo,
    StyledWrapper,
    videoClassname,
} from '../../content/Video/Video.styled'
import { TextLink } from '../../typography/TextLink/TextLink'
import { Carousel } from '../Carousel/Carousel'
import {
    StyledVideoPlayerByline,
    StyledVideoPlayerDescription,
    StyledVideoPlayerDescriptionMoreLess,
    StyledVideoPlayerDescriptionMoreLessButton,
    StyledVideoPlayerHeadline,
    StyledVideoPlayerTextContainer,
    StyledVideoPlayerWrapper,
    StyledProfileLinkContainer,
    StyledProfileLink,
} from '../VideoPlayer/VideoPlayer.styled'
import { VideoMetaDTO } from '@west-australian-newspapers/publication-types'
export const videoWrapperClassName = 'video-wrapper'

const getVideoPlayerDescriptionWithLinks = (description: string) => {
    const doc = new DOMParser().parseFromString(description, 'text/html')
    const mappedTextWithLinkComponents = [...doc.body.childNodes].map(
        (el, i) => {
            if (el.nodeName === 'A') {
                const linkNode = el as HTMLAnchorElement
                return (
                    <TextLink
                        key={i}
                        href={linkNode.href}
                        openInNewWindow={true}
                    >
                        {linkNode.textContent}
                    </TextLink>
                )
            } else {
                return el.textContent
            }
        },
    )

    return <React.Fragment>{mappedTextWithLinkComponents}</React.Fragment>
}

export const VideoHubVideoSkin: React.FC<RenderPlayerParams> = ({
    theme,
    wrapperElProps,
    videoElement,
    playlistProps,
    playerState,
    playerOptions,
    playerActions,
    hideSticky,
    setHideSticky,
    publicUrl,
    onPlayNextVideo,
}) => {
    const [isShowMore, setIsShowMore] = useState(true)
    const isMobileBauhaus =
        playerState.showInternalPlayButton &&
        isBauhausSection(theme.section as SevenNewsSection) &&
        isMobileViewport()

    //prevent displaying full description on video change
    useEffect(() => {
        setIsShowMore(true)
    }, [playerState.currentVideo])

    // if devices is mobile(autoplay disabled) AND has an interal play button AND has no play button text
    // AND is on sevennews AND is not a bauhaus themed section
    const isMobileSeven =
        !playerState.canAutoplay &&
        playerState.showInternalPlayButton &&
        !playerOptions.playButtonText &&
        theme.kind === Product.SevenNews &&
        !isBauhausSection(theme.section as SevenNewsSection)

    // variable used to display video title on load on mobile seven (including bauhaus)
    const showVideoTitle = isMobileBauhaus || isMobileSeven

    const renderUnmuteButton = playerState.isMuted &&
        !playerState.showTitleOverlay &&
        (theme.kind === Product.SevenNews
            ? true
            : !playerState.showInternalPlayButton) && (
            <UnmuteButton
                positionState={{
                    alignToTop: playerOptions.compactMode,
                    isLowered:
                        theme.kind === Product.SevenNews
                            ? false
                            : (!playerState.showTopBar &&
                                  playerState.adPlayed) ||
                              (!playerState.showTopBar &&
                                  playerState.adPlaying),
                }}
                action={playerActions.handleMuteToggle}
            />
        )

    const renderPlayOverlay = !playerState.showInternalPlayButton ? (
        false
    ) : (
        <PlayOverlay
            play={playerActions.playVideo}
            isCentered={true}
            isSmall={playerOptions.smallPlayButton}
        />
    )

    const renderTitleOverlay = (showVideoTitle ||
        playerState.showTitleOverlay) && (
        <TitleOverlay
            showVideoTitle={showVideoTitle}
            play={playerActions.playVideo}
            cancelAutoplay={playerActions.stopAutoplay}
            videoDuration={
                playerState.currentVideoMetadata
                    ? getHumanDuration(
                          playerState.currentVideoMetadata.duration,
                      )
                    : undefined
            }
            compactMode={playerOptions.compactMode || false}
            videoDetails={playerState.currentVideo}
            autoplayRemainingTime={
                playerState.currentState.type === 'autoplay-countdown'
                    ? playerState.currentState.remainingTime
                    : undefined
            }
        />
    )

    const { innerRef, ...StyledVideoWrapperProps } = wrapperElProps
    const posterImageCrop = getPosterImage(playerState.currentVideo)
    let posterImageScaled: string | undefined

    if (posterImageCrop) {
        posterImageScaled = scaleImage(
            posterImageCrop.reference,
            getImageWidth(theme.kind),
            false,
        )
    }

    if (playlistProps && playerState.currentVideoMetadata) {
        const currentVideoItem = mapListVideo(
            playerState.currentVideoMetadata,
            { publicUrl: publicUrl ?? '' },
        )

        return (
            <Fragment>
                {posterImageScaled && (
                    <style
                        dangerouslySetInnerHTML={{
                            __html: `
                                @media print {
                                    .${videoClassname}::before {
                                        content: url("${posterImageScaled}");
                                        max-width: 600px;
                                    }
                                }`,
                        }}
                    />
                )}
                <StyledWrapper
                    className={videoWrapperClassName}
                    compactMode={playerOptions.compactMode}
                    hide={hideSticky && playerOptions.compactMode}
                    isShowMore={isShowMore}
                    isVideoHub={true}
                >
                    <StyledVideoPlayerWrapper>
                        <StyledVideo
                            {...StyledVideoWrapperProps}
                            ref={innerRef}
                            posterImage={posterImageScaled}
                        >
                            {renderPlayOverlay}
                            {renderTitleOverlay}
                            {videoElement}
                            {renderUnmuteButton}
                        </StyledVideo>
                        <StyledVideoPlayerTextContainer>
                            {theme.kind === 'perthnow' && (
                                <StyledKicker
                                    style={{
                                        textTransform: 'capitalize',
                                    }}
                                    size={'large'}
                                >
                                    {currentVideoItem.primaryTopic.title}
                                </StyledKicker>
                            )}
                            {theme.kind === 'thewest' && (
                                <StyledKicker
                                    style={{
                                        textTransform: 'uppercase',
                                    }}
                                    size={'large'}
                                >
                                    {currentVideoItem.primaryTopic.title}
                                    <StyledKickerText>
                                        <IconVideo2
                                            width={14}
                                            height={9}
                                            horizontalSpacing={6}
                                            className={StyledIconVideo2}
                                        />
                                    </StyledKickerText>
                                </StyledKicker>
                            )}
                            {theme.kind === 'sevennews' && (
                                <StyledKicker>
                                    {currentVideoItem.primaryTopic.title}
                                </StyledKicker>
                            )}
                            <StyledVideoPlayerHeadline>
                                {currentVideoItem.headline}
                            </StyledVideoPlayerHeadline>
                            {theme.kind === 'sevennews' ? (
                                <SevenVideoDescription
                                    description={currentVideoItem.teaser}
                                    showMore={isShowMore}
                                    showMoreHandler={() =>
                                        setIsShowMore((prev) => !prev)
                                    }
                                />
                            ) : (
                                <StyledVideoPlayerDescription>
                                    {getVideoPlayerDescriptionWithLinks(
                                        currentVideoItem.teaser,
                                    )}
                                </StyledVideoPlayerDescription>
                            )}
                            <StyledVideoPlayerByline>
                                {theme.kind === 'sevennews' && (
                                    <SevenVideoProfileLink
                                        profiles={currentVideoItem.profiles}
                                    />
                                )}
                                <StyledSharingWrap className="StyledSharingWrap">
                                    <Sharing
                                        className="BauhausCardSharing"
                                        url={currentVideoItem._self}
                                        text={currentVideoItem.headline}
                                        isGhostStyle={true}
                                        onEvent={() => {}}
                                        shareOptions={[
                                            'facebook',
                                            'twitter',
                                            'clipboard',
                                        ]}
                                        isBauhaus={true}
                                    />
                                </StyledSharingWrap>
                            </StyledVideoPlayerByline>
                        </StyledVideoPlayerTextContainer>
                    </StyledVideoPlayerWrapper>
                    {playlistProps &&
                        playlistProps.initialVideoMetadata &&
                        playerState.currentVideoMetadata && (
                            <Carousel
                                onEvent={() => {}}
                                itemMeta={
                                    playlistProps.videoQueue.items
                                        ? playlistProps.videoQueue.items
                                        : []
                                }
                                currentVideo={currentVideoItem}
                                currentPath={'/'}
                                onSelect={playlistProps.handlePlaylistSelect}
                            />
                        )}
                </StyledWrapper>
            </Fragment>
        )
    } else {
        return null
    }
}

const SevenVideoDescription: React.FC<{
    description: string
    showMore: boolean
    showMoreHandler: () => void
}> = ({ description, showMore, showMoreHandler }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [maxChars, setMaxChars] = useState(100)
    const [des, setDes] = useState(
        getVideoPlayerDescriptionWithLinks(description),
    )

    useEffect(() => {
        const calculateMaxCharacters = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth
                const charWidth = 10
                const maxCharactersPerLine = Math.floor(
                    containerWidth / charWidth,
                )
                const maxCharacters = maxCharactersPerLine * 2
                setMaxChars(maxCharacters - 12)
            }
        }

        calculateMaxCharacters()
        window.addEventListener('resize', calculateMaxCharacters)
        return () => {
            window.removeEventListener('resize', calculateMaxCharacters)
        }
    }, [])
    useEffect(() => {
        setDes(
            getVideoPlayerDescriptionWithLinks(
                showMore ? description.slice(0, maxChars) : description,
            ),
        )
    }, [showMore, description, maxChars])

    return (
        <StyledVideoPlayerDescriptionMoreLess ref={containerRef}>
            {des}{' '}
            {description.length > maxChars && (
                <StyledVideoPlayerDescriptionMoreLessButton
                    onClick={showMoreHandler}
                >
                    {showMore ? '...view more' : ' view less'}
                </StyledVideoPlayerDescriptionMoreLessButton>
            )}
        </StyledVideoPlayerDescriptionMoreLess>
    )
}

const SevenVideoProfileLink: React.FC<{
    profiles: Profile[] | undefined
}> = ({ profiles }) => {
    return (
        <StyledProfileLinkContainer>
            {profiles && profiles.length > 0 ? (
                <>
                    {profiles.map((profile, i, arr) => {
                        const hasMoreProfileLinks = arr.length > i + 1
                        return (
                            <>
                                <StyledProfileLink
                                    to={'/profile/' + profile.slug}
                                    key={i}
                                >
                                    {i === 0 && 'By '}
                                    {`${profile.name}`}
                                </StyledProfileLink>
                                {hasMoreProfileLinks && ' & '}
                            </>
                        )
                    })}
                </>
            ) : (
                'By Digital Team'
            )}
        </StyledProfileLinkContainer>
    )
}
